import { useState } from "react";
import { Day, Category } from "./EventPage";
import { 
  Search, Calendar, Grid, Trophy, Code, 
  Users, Gamepad2, Filter, X, ChevronDown, ChevronUp,
  XCircle
} from "lucide-react";

interface UnifiedFiltersProps {
  selectedDay: Day;
  selectedCategory: Category;
  onDayChange: (day: Day) => void;
  onCategoryChange: (category: Category) => void;
  onSearchChange: (query: string) => void;
  searchQuery: string;
  showAllEvents: () => void;
  clearDayFilter: () => void;
  clearCategoryFilter: () => void;
  clearSearchFilter: () => void;
}

const categories: { key: Category; label: string; icon: React.ElementType }[] = [
  { key: "all", label: "All Categories", icon: Grid },
  { key: "flagship", label: "Flagship", icon: Trophy },
  { key: "technical", label: "Technical", icon: Code },
  { key: "non-technical", label: "Non-Technical", icon: Users },
  { key: "games", label: "Games", icon: Gamepad2 },
];

const days = [
  { key: "all" as Day, label: "All Days", icon: Calendar },
  { key: "day1" as Day, label: "Day 1", icon: Calendar },
  { key: "day2" as Day, label: "Day 2", icon: Calendar },
] as const;

const styles = `
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slide-down {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export const UnifiedFilters = ({
  selectedDay,
  selectedCategory,
  onDayChange,
  onCategoryChange,
  onSearchChange,
  searchQuery,
  showAllEvents,
  clearDayFilter,
  clearCategoryFilter,
  clearSearchFilter
}: UnifiedFiltersProps) => {
  const [showFilters, setShowFilters] = useState(false);

  // Check if any filters are active (not "all" or empty)
  const isDayFilterActive = selectedDay !== "all";
  const isCategoryFilterActive = selectedCategory !== "all";
  const isSearchFilterActive = searchQuery.trim().length > 0;
  
  // Show active filters if any condition is true
  const showActiveFilters = isDayFilterActive || isCategoryFilterActive || isSearchFilterActive;

  return (
    <>
      <style>{styles}</style>
      
      {/* Main Compact Filter Bar */}
      <div 
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '24px',
          boxShadow: '0 2px 10px rgba(var(--color-primary-rgb, 59, 130, 246), 0.06)',
          animation: 'fade-in 0.4s ease-out',
        }}
      >
        {/* Top Row: Search + Filters Button + Clear All */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
          }}
        >
          {/* Search Bar */}
          <div 
            style={{
              flex: '1',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Search 
              style={{
                position: 'absolute',
                left: '12px',
                color: 'var(--color-text-muted)',
                width: '16px',
                height: '16px',
              }}
            />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                backgroundColor: 'var(--color-background)',
                color: 'var(--color-text)',
                transition: 'all 0.2s ease',
                outline: 'none',
                height: '40px',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(var(--color-primary-rgb, 59, 130, 246), 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  padding: '2px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Show Filters Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: showFilters ? 'var(--color-primary)' : 'var(--color-background-alt)',
              border: `1px solid ${showFilters ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)',
              color: showFilters ? 'white' : 'var(--color-text)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              height: '40px',
            }}
            onMouseEnter={(e) => {
              if (!showFilters) {
                e.currentTarget.style.backgroundColor = 'rgba(var(--color-primary-rgb, 59, 130, 246), 0.1)';
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.color = 'var(--color-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showFilters) {
                e.currentTarget.style.backgroundColor = 'var(--color-background-alt)';
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.color = 'var(--color-text)';
              }
            }}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Clear All Button (only shows when filters are active) */}
          {showActiveFilters && (
            <button
              onClick={showAllEvents}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: 'rgba(var(--color-accent-rgb, 139, 92, 246), 0.1)',
                border: '1px solid var(--color-accent)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-accent)',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                height: '40px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(var(--color-accent-rgb, 139, 92, 246), 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(var(--color-accent-rgb, 139, 92, 246), 0.1)';
                e.currentTarget.style.color = 'var(--color-accent)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <XCircle size={14} />
              Clear All
            </button>
          )}
        </div>

        {/* Filters Dropdown */}
        {showFilters && (
          <div 
            style={{
              padding: '16px',
              borderTop: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-background-alt)',
              animation: 'slide-down 0.3s ease-out',
            }}
          >
            <div 
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px',
              }}
            >
              {/* Day Selector */}
              <div>
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar 
                      style={{
                        color: 'var(--color-primary)',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                    <span 
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--color-text)',
                      }}
                    >
                      Select Day
                    </span>
                  </div>
                  {isDayFilterActive && (
                    <button
                      onClick={clearDayFilter}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: 'rgba(var(--color-primary-rgb, 59, 130, 246), 0.1)',
                        border: '1px solid rgba(var(--color-primary-rgb, 59, 130, 246), 0.3)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-primary)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <X size={10} />
                      Clear
                    </button>
                  )}
                </div>
                
                <div 
                  style={{
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  {days.map(({ key, label }) => {
                    const isSelected = selectedDay === key;
                    return (
                      <button
                        key={key}
                        onClick={() => onDayChange(key)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: 'var(--radius-sm)',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: isSelected 
                            ? 'var(--color-primary)' 
                            : 'var(--color-background)',
                          border: isSelected 
                            ? `1px solid var(--color-primary)` 
                            : `1px solid var(--color-border)`,
                          color: isSelected ? 'white' : 'var(--color-text-secondary)',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'rgba(var(--color-primary-rgb, 59, 130, 246), 0.1)';
                            e.currentTarget.style.borderColor = 'var(--color-primary)';
                            e.currentTarget.style.color = 'var(--color-primary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'var(--color-background)';
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                            e.currentTarget.style.color = 'var(--color-text-secondary)';
                          }
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category Selector */}
              <div>
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Grid 
                      style={{
                        color: 'var(--color-secondary)',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                    <span 
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--color-text)',
                      }}
                    >
                      Event Category
                    </span>
                  </div>
                  {isCategoryFilterActive && (
                    <button
                      onClick={clearCategoryFilter}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: 'rgba(var(--color-secondary-rgb, 29, 78, 216), 0.1)',
                        border: '1px solid rgba(var(--color-secondary-rgb, 29, 78, 216), 0.3)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-secondary)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <X size={10} />
                      Clear
                    </button>
                  )}
                </div>
                
                <div 
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {categories.map(({ key, label, icon: Icon }) => {
                    const isSelected = selectedCategory === key;
                    const categoryColors = getCategoryColors(key);
                    
                    return (
                      <button
                        key={key}
                        onClick={() => onCategoryChange(key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          borderRadius: 'var(--radius-sm)',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: isSelected 
                            ? categoryColors.primary 
                            : 'var(--color-background)',
                          border: isSelected 
                            ? `1px solid ${categoryColors.primary}` 
                            : `1px solid var(--color-border)`,
                          color: isSelected ? 'white' : 'var(--color-text-secondary)',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = categoryColors.light;
                            e.currentTarget.style.borderColor = categoryColors.primary;
                            e.currentTarget.style.color = categoryColors.primary;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'var(--color-background)';
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                            e.currentTarget.style.color = 'var(--color-text-secondary)';
                          }
                        }}
                      >
                        <Icon 
                          style={{
                            width: '14px',
                            height: '14px',
                          }}
                        />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {showActiveFilters && (
              <div 
                style={{
                  marginTop: '20px',
                  paddingTop: '16px',
                  borderTop: '1px dashed var(--color-border)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                <span 
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Active filters:
                </span>
                
                {isDayFilterActive && (
                  <div 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      backgroundColor: 'rgba(var(--color-primary-rgb, 59, 130, 246), 0.1)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.75rem',
                    }}
                  >
                    <Calendar size={12} />
                    <span>
                      {selectedDay === 'day1' ? 'Day 1' : 'Day 2'}
                    </span>
                    <button
                      onClick={clearDayFilter}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-muted)',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
                
                {isCategoryFilterActive && (
                  <div 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      backgroundColor: 'rgba(var(--color-secondary-rgb, 29, 78, 216), 0.1)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.75rem',
                    }}
                  >
                    <Grid size={12} />
                    <span style={{ textTransform: 'capitalize' }}>
                      {selectedCategory}
                    </span>
                    <button
                      onClick={clearCategoryFilter}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-muted)',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
                
                {isSearchFilterActive && (
                  <div 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      backgroundColor: 'rgba(var(--color-accent-rgb, 139, 92, 246), 0.1)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.75rem',
                    }}
                  >
                    <Search size={12} />
                    <span>"{searchQuery}"</span>
                    <button
                      onClick={clearSearchFilter}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-muted)',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// Helper function to get category colors based on theme
function getCategoryColors(category: Category): { primary: string; light: string } {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  
  // Special color for "all" category
  if (category === "all") {
    return {
      primary: 'var(--color-accent)',
      light: 'rgba(var(--color-accent-rgb, 139, 92, 246), 0.1)'
    };
  }
  
  if (isDark) {
    // Dark mode colors
    const colors = {
      flagship: { primary: '#ef4444', light: 'rgba(239, 68, 68, 0.15)' },
      technical: { primary: '#3b82f6', light: 'rgba(59, 130, 246, 0.15)' },
      'non-technical': { primary: '#8b5cf6', light: 'rgba(139, 92, 246, 0.15)' },
      games: { primary: '#10b981', light: 'rgba(16, 185, 129, 0.15)' },
    };
    return colors[category];
  } else {
    // Light mode colors
    const colors = {
      flagship: { primary: '#dc2626', light: 'rgba(220, 38, 38, 0.1)' },
      technical: { primary: '#2563eb', light: 'rgba(37, 99, 235, 0.1)' },
      'non-technical': { primary: '#7c3aed', light: 'rgba(124, 58, 237, 0.1)' },
      games: { primary: '#059669', light: 'rgba(5, 150, 105, 0.1)' },
    };
    return colors[category];
  }
}